import { API_BASE_URL, API_ORIGIN } from '../config/api';

// Helper function to get default headers (no Authorization; using httpOnly cookie)
const getJsonHeaders = () => ({
  'Content-Type': 'application/json'
});

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Network error' }));
    const error = new Error(errorData.error || 'API request failed');
    // Add error type if available
    if (errorData.errorType) {
      error.errorType = errorData.errorType;
    }
    throw error;
  }
  return response.json();
};

// Authentication API
export const authAPI = {
  getConfig: async () => {
    const response = await fetch(`${API_BASE_URL}/auth/config`);
    return handleResponse(response);
  },

  // Outside /api/v1: its callback path must match the Azure app registration's
  // redirect URI exactly, so it lives at the fixed path the backend registers.
  entraLoginUrl: `${API_ORIGIN}/api/auth/oidc/login`,

  login: async (username, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username, password })
    });
    
    const data = await handleResponse(response);
    
    // Token is set in httpOnly cookie by server; nothing to store client-side
    
    return data;
  },

  register: async (username, email, password, firstName, lastName) => {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        username, 
        email, 
        password, 
        first_name: firstName,
        last_name: lastName
      })
    });
    
    return handleResponse(response);
  },

  logout: async () => {
    // Clear cookie on server
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (_) {
      // ignore network errors on logout
    }
  },

  // Deprecated: with httpOnly cookies, the client cannot check auth state locally.
  // Prefer probing /user/me from the app.
  isAuthenticated: () => false
};

// User API
export const userAPI = {
  getAllUsers: async () => {
    const response = await fetch(`${API_BASE_URL}/users`, {
      headers: {},
      credentials: 'include'
    });
    
    const data = await handleResponse(response);
    return data.users || [];
  },

  getDashboardUsers: async (year, month) => {
    const response = await fetch(`${API_BASE_URL}/dashboard/users?year=${year}&month=${month}`, {
      headers: {},
      credentials: 'include'
    });
    
    const data = await handleResponse(response);
    return data.users || [];
  },

  getCurrentUserInfo: async () => {
    const response = await fetch(`${API_BASE_URL}/user/me`, {
      headers: {},
      credentials: 'include'
    });
    
    const data = await handleResponse(response);
    return data.user;
  },

  getVacationBalance: async () => {
    const response = await fetch(`${API_BASE_URL}/vacation-balance`, {
      headers: {},
      credentials: 'include'
    });
    
    return handleResponse(response);
  },

  getVacationBalanceForYear: async (year) => {
    const response = await fetch(`${API_BASE_URL}/vacation-balance/${year}`, {
      headers: {},
      credentials: 'include'
    });
    
    return handleResponse(response);
  }
};

// Holiday API
const getHolidays = async (year) => {
  const response = await fetch(`${API_BASE_URL}/holidays?year=${year}`, {
    method: 'GET',
    headers: {},
    credentials: 'include'
  });
  return (await handleResponse(response)).holidays;
};

export const holidayAPI = {
  getHolidays
};

// Year Status API
const getYearStatus = async () => {
  const response = await fetch(`${API_BASE_URL}/year-status`, {
    headers: {},
    credentials: 'include'
  });
  return (await handleResponse(response)).year_status;
};

export const yearStatusAPI = {
  getYearStatus
};

// Work Status API
export const workStatusAPI = {
  getWorkStatuses: async (startDate, endDate) => {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate
    });
    
    const response = await fetch(`${API_BASE_URL}/work-statuses?${params}`, {
      headers: {},
      credentials: 'include'
    });
    
    const data = await handleResponse(response);
    return data.statuses || [];
  },

  setWorkStatus: async (date, status, options = {}) => {
    const { attachment } = options;
    let response;

    if (attachment) {
      const formData = new FormData();
      formData.append('date', date);
      formData.append('status', status);
      formData.append('attachment', attachment);

      response = await fetch(`${API_BASE_URL}/work-status`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
    } else {
      response = await fetch(`${API_BASE_URL}/work-status`, {
        method: 'POST',
        headers: getJsonHeaders(),
        credentials: 'include',
        body: JSON.stringify({ date, status })
      });
    }

    return handleResponse(response);
  },

  setWorkStatusBatch: async (dates, status, options = {}) => {
    const { attachment } = options;
    let response;

    if (attachment) {
      const formData = new FormData();
      dates.forEach(date => formData.append('dates[]', date));
      formData.append('status', status);
      formData.append('attachment', attachment);

      response = await fetch(`${API_BASE_URL}/work-status/batch`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
    } else {
      response = await fetch(`${API_BASE_URL}/work-status/batch`, {
        method: 'POST',
        headers: getJsonHeaders(),
        credentials: 'include',
        body: JSON.stringify({ dates, status })
      });
    }

    return handleResponse(response);
  },

  deleteWorkStatus: async (date) => {
    const response = await fetch(`${API_BASE_URL}/work-status/${date}`, {
      method: 'DELETE',
      headers: {},
      credentials: 'include'
    });
    
    return handleResponse(response);
  },

  deleteWorkStatusBatch: async (dates) => {
    const response = await fetch(`${API_BASE_URL}/work-status/batch`, {
      method: 'DELETE',
      headers: getJsonHeaders(),
      credentials: 'include',
      body: JSON.stringify({ dates })
    });
    
    return handleResponse(response);
  },

  // Admin-specific endpoints for editing other users' work statuses
  adminSetWorkStatus: async (date, status, username, options = {}) => {
    const { attachment } = options;
    let response;

    if (attachment) {
      const formData = new FormData();
      formData.append('date', date);
      formData.append('status', status);
      formData.append('username', username);
      formData.append('attachment', attachment);

      response = await fetch(`${API_BASE_URL}/admin/work-status`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
    } else {
      response = await fetch(`${API_BASE_URL}/admin/work-status`, {
        method: 'POST',
        headers: getJsonHeaders(),
        credentials: 'include',
        body: JSON.stringify({ date, status, username })
      });
    }

    return handleResponse(response);
  },

  adminSetWorkStatusBatch: async (dates, status, username, options = {}) => {
    const { attachment } = options;
    let response;

    if (attachment) {
      const formData = new FormData();
      dates.forEach(date => formData.append('dates[]', date));
      formData.append('status', status);
      formData.append('username', username);
      formData.append('attachment', attachment);

      response = await fetch(`${API_BASE_URL}/admin/work-status/batch`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
    } else {
      response = await fetch(`${API_BASE_URL}/admin/work-status/batch`, {
        method: 'POST',
        headers: getJsonHeaders(),
        credentials: 'include',
        body: JSON.stringify({ dates, status, username })
      });
    }

    return handleResponse(response);
  },

  adminDeleteWorkStatus: async (date, username) => {
    const response = await fetch(`${API_BASE_URL}/admin/work-status`, {
      method: 'DELETE',
      headers: getJsonHeaders(),
      credentials: 'include',
      body: JSON.stringify({ date, username })
    });
    
    return handleResponse(response);
  },

  adminDeleteWorkStatusBatch: async (dates, username) => {
    const response = await fetch(`${API_BASE_URL}/admin/work-status/batch`, {
      method: 'DELETE',
      headers: getJsonHeaders(),
      credentials: 'include',
      body: JSON.stringify({ dates, username })
    });
    
    return handleResponse(response);
  }
};
